import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, ArrowRight, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingCTA from "../components/FloatingCTA";
import { characters, OUTBOUND_URL, SITE_URL } from "../data/characters";

const CharacterPage = () => {
  const { id } = useParams();
  const character = characters.find((c) => c.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!character) {
    return <Navigate to="/" replace />;
  }

  const seo = character.seo;
  const url = `${SITE_URL}/${character.id}`;
  const related = characters.filter((c) => c.id !== character.id).slice(0, 3);

  // BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${SITE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "AI Characters",
        item: `${SITE_URL}/#characters`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: `${character.name} — ${character.archetype}`,
        item: url,
      },
    ],
  };

  // Person schema (for the AI character)
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: character.name,
    alternateName: `${character.name} — ${character.archetype}`,
    description: character.description,
    image: `${SITE_URL}${character.image}`,
    url,
    jobTitle: character.archetype,
    knowsAbout: character.traits,
  };

  // FAQPage for the per-character starters
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: seo.starters.map((q) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: {
        "@type": "Answer",
        text: `Start a conversation with ${character.name} and get a real-time AI girlfriend reply. ${character.name} adapts to your tone and remembers context across the chat.`,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{seo.metaTitle}</title>
        <meta name="description" content={seo.metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={seo.metaTitle} />
        <meta property="og:description" content={seo.metaDescription} />
        <meta property="og:image" content={`${SITE_URL}${character.image}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.metaTitle} />
        <meta name="twitter:description" content={seo.metaDescription} />
        <meta name="twitter:image" content={`${SITE_URL}${character.image}`} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(personSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div
        data-testid={`character-page-${character.id}`}
        className="bg-white text-black"
      >
        <Header />

        {/* Breadcrumbs */}
        <nav
          data-testid="breadcrumbs"
          aria-label="Breadcrumb"
          className="pt-24 sm:pt-28 pb-2"
        >
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <ol className="flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.18em] uppercase font-body text-neutral-600">
              <li>
                <Link
                  to="/"
                  className="hover:text-black transition"
                  data-testid="breadcrumb-home"
                >
                  Home
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5" />
              </li>
              <li>
                <Link
                  to="/#characters"
                  className="hover:text-black transition"
                  data-testid="breadcrumb-characters"
                >
                  AI Characters
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5" />
              </li>
              <li className="text-black" aria-current="page">
                {character.name}
              </li>
            </ol>
          </div>
        </nav>

        {/* Hero banner */}
        <section
          data-testid="character-hero"
          className="relative w-full overflow-hidden border-b-2 border-black"
          style={{
            backgroundColor: character.primary,
            color: character.textOnPrimary,
          }}
        >
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12 pt-10 sm:pt-16 pb-16 sm:pb-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
            <div>
              <span className="text-xs sm:text-sm font-bold tracking-[0.25em] uppercase opacity-90 font-body">
                AI Character · {character.archetype}
              </span>
              <h1
                data-testid="character-h1"
                className="font-display mt-4 text-4xl sm:text-6xl lg:text-7xl xl:text-[5rem] font-black tracking-tighter uppercase leading-[0.92] break-words"
              >
                {seo.h1}
              </h1>
              <p className="font-display mt-6 text-xl sm:text-2xl font-bold opacity-95 max-w-xl">
                {character.tagline}
              </p>

              <ul className="mt-8 flex flex-wrap gap-2">
                {character.traits.map((t) => (
                  <li
                    key={t}
                    className="text-xs sm:text-sm font-bold tracking-wider uppercase border-2 border-current px-3 py-1.5"
                  >
                    {t}
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  data-testid="character-page-cta"
                  href={OUTBOUND_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ "--shadow-col": character.secondary }}
                  className="char-cta inline-flex items-center gap-2 bg-black text-white font-display font-black uppercase tracking-tight text-base sm:text-lg px-7 py-4 border-2 border-black"
                >
                  Chat with {character.name} now
                  <ArrowUpRight className="w-5 h-5" />
                </a>
                <span className="text-xs font-bold tracking-[0.2em] uppercase opacity-90">
                  Instant · Always on
                </span>
              </div>
            </div>

            <div className="relative w-full">
              <div className="relative w-full max-w-[560px] ml-auto aspect-[3/4]">
                <a
                  data-testid="character-page-image-cta"
                  href={OUTBOUND_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Chat with ${character.name}`}
                  className="block w-full h-full"
                >
                  <img
                    src={character.image}
                    alt={character.alt}
                    width="560"
                    height="746"
                    loading="eager"
                    decoding="async"
                    className="char-img w-full h-full object-cover"
                  />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Long-form SEO body */}
        <section
          data-testid="character-seo-body"
          className="bg-white py-20 sm:py-28"
        >
          <div className="max-w-[1100px] mx-auto px-6 sm:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
            <article className="md:col-span-8">
              <p className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-12">
                {seo.intro}
              </p>

              {seo.sections.map((s, i) => (
                <div key={i} className="mt-10">
                  <h2
                    data-testid={`character-section-h2-${i}`}
                    className="font-display text-3xl sm:text-4xl font-black tracking-tight uppercase leading-[0.95]"
                  >
                    {s.h2}
                  </h2>
                  <p className="font-body mt-5 text-lg text-neutral-800 leading-relaxed">
                    {s.body}
                  </p>
                </div>
              ))}

              {/* Conversation starters */}
              <div className="mt-16">
                <h2
                  data-testid="character-starters-h2"
                  className="font-display text-3xl sm:text-4xl font-black tracking-tight uppercase leading-[0.95]"
                >
                  Conversation starters with {character.name}
                </h2>
                <p className="font-body mt-4 text-neutral-700 leading-relaxed">
                  Stuck on the first message? Try one of these — every line
                  works as a real opener for your first character AI chat with{" "}
                  {character.name}.
                </p>
                <ul
                  data-testid="character-starters"
                  className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  {seo.starters.map((s, i) => (
                    <li key={i}>
                      <a
                        data-testid={`character-starter-${i}`}
                        href={OUTBOUND_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block border-2 border-black p-4 font-body text-base sm:text-lg leading-snug hover:bg-black hover:text-white transition"
                      >
                        <span className="font-display font-black text-xs tracking-[0.2em] uppercase block mb-1 text-neutral-500 group-hover:text-white">
                          Try
                        </span>
                        “{s}”
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div
                data-testid="mid-cta"
                className="mt-16 border-2 border-black p-8 sm:p-10"
                style={{ backgroundColor: character.primary, color: character.textOnPrimary }}
              >
                <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tighter uppercase leading-[0.95]">
                  Ready to chat with {character.name}?
                </h3>
                <p className="font-body mt-3 max-w-xl text-base sm:text-lg opacity-95">
                  Just pick a message and start the conversation — replies
                  arrive in under a second.
                </p>
                <a
                  data-testid="character-page-cta-2"
                  href={OUTBOUND_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brutal-shadow mt-6 inline-flex items-center gap-3 bg-black text-white font-display font-black uppercase tracking-tight text-base sm:text-lg px-7 py-4 border-2 border-black"
                >
                  Start chatting now
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </article>

            {/* Sidebar — quick facts */}
            <aside className="md:col-span-4">
              <div className="sticky top-24 border-2 border-black p-6 sm:p-7 bg-neutral-50">
                <span className="text-xs font-bold tracking-[0.25em] uppercase font-body text-neutral-500">
                  Quick facts
                </span>
                <h3 className="font-display mt-3 text-2xl font-black uppercase tracking-tight">
                  About {character.name}
                </h3>
                <dl className="mt-6 space-y-4 text-sm">
                  <div className="flex justify-between gap-4 border-b border-neutral-200 pb-3">
                    <dt className="font-bold tracking-wider uppercase text-neutral-500">
                      Archetype
                    </dt>
                    <dd className="text-right font-display font-black uppercase">
                      {character.archetype}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-neutral-200 pb-3">
                    <dt className="font-bold tracking-wider uppercase text-neutral-500">
                      Vibe
                    </dt>
                    <dd className="text-right">{character.traits[0]}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-neutral-200 pb-3">
                    <dt className="font-bold tracking-wider uppercase text-neutral-500">
                      Avg reply
                    </dt>
                    <dd className="text-right">0.4s</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-neutral-200 pb-3">
                    <dt className="font-bold tracking-wider uppercase text-neutral-500">
                      Memory
                    </dt>
                    <dd className="text-right">Long-term</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="font-bold tracking-wider uppercase text-neutral-500">
                      Style
                    </dt>
                    <dd className="text-right font-display font-black">
                      Roleplay
                    </dd>
                  </div>
                </dl>
                <a
                  data-testid="sidebar-cta"
                  href={OUTBOUND_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brutal-shadow mt-7 w-full inline-flex items-center justify-center gap-2 bg-black text-white font-display font-black uppercase tracking-tight text-sm px-5 py-3 border-2 border-black"
                >
                  Chat now →
                </a>
              </div>
            </aside>
          </div>
        </section>

        {/* Related characters */}
        <section
          data-testid="related-characters"
          className="bg-[#0A0A0A] text-white py-20 sm:py-24 border-t-2 border-black"
        >
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
            <span className="text-xs sm:text-sm font-bold tracking-[0.25em] uppercase font-body text-[#00E5FF]">
              More AI girls
            </span>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-[0.95]">
              Not feeling {character.name}? Try these.
            </h2>

            <ul className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/${r.id}`}
                    data-testid={`related-${r.id}`}
                    className="group block border-2 border-white/20 hover:border-white p-6 transition"
                    style={{ backgroundColor: r.primary }}
                  >
                    <div className="aspect-[4/5] overflow-hidden">
                      <img
                        src={r.image}
                        alt={r.alt}
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="500"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div
                      style={{ color: r.textOnPrimary }}
                      className="mt-5"
                    >
                      <span className="text-xs font-bold tracking-[0.2em] uppercase opacity-90">
                        {r.archetype}
                      </span>
                      <h3 className="font-display mt-2 text-3xl font-black uppercase tracking-tighter">
                        {r.name}
                      </h3>
                      <p className="font-body mt-2 text-sm opacity-95 line-clamp-2">
                        {r.tagline}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-display font-black uppercase tracking-tight">
                        See profile <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Footer />
        <FloatingCTA />
      </div>
    </>
  );
};

export default CharacterPage;
